import * as THREE from 'three';
import {OperationGroup} from 'components/customer/Preview3D/types';
import type {CabinetMesh} from 'components/customer/RoomPlanner/types';

export type DoorFrontsMode = 'shut' | 'open' | 'hide';

export type DoorFrontsPose = Exclude<DoorFrontsMode, 'hide'>;

/**
 * Icon for the current fronts pose. Hide keeps that pose in gray.
 * @param {DoorFrontsMode} mode - Current fronts mode
 * @param {DoorFrontsPose} pose - Last shut or open pose, used while hidden
 * @return {string} Icon path for the open or closed fronts
 */
export const frontsIconName = (
    mode: DoorFrontsMode,
    pose: DoorFrontsPose
): string => {
    const open = (mode === 'hide' ? pose : mode) === 'open';

    if (mode === 'hide') {
        return open
            ? 'preview/Drawers-Open-Gray.svg'
            : 'preview/Drawers-Closed-Gray.svg';
    }

    return open ? 'preview/Drawers-Open.svg' : 'preview/Drawers-Closed.svg';
};

/**
 * @param {string} value - data-mode from a Fronts button
 * @return {boolean} True when the value is shut, open, or hide
 */
export const isDoorFrontsMode = (value: string): value is DoorFrontsMode => {
    return value === 'shut' || value === 'open' || value === 'hide';
};

export interface DoorPoseParams {
    doorGap?: number;
    isDoorOrDrawerOpen: boolean;
    animationSpeed?: number;
}

export type DoorPoseTrigger = (
    cabinet: CabinetMesh,
    eventName: 'toggleDoor',
    params: DoorPoseParams
) => Promise<void> | void;

/**
 * Door and drawer fronts, including handles modelled on those panels.
 * Drawer boxes, runners, shelves and carcase panels are not fronts.
 * @param {Partial<OperationGroup>} operationGroup - Panel operation to classify.
 * @return {boolean} True when the panel is a door or drawer front.
 */
export const isDoorOrDrawerFront = (
    operationGroup: Partial<OperationGroup>
): boolean => {
    const partId = (operationGroup.PartID ?? '').toLowerCase();

    return Boolean(
        operationGroup.IsLeftDoor ||
        operationGroup.IsRightDoor ||
        operationGroup.IsPairDoor ||
        operationGroup.IsCornerLeftDoor ||
        operationGroup.IsCornerRightDoor ||
        operationGroup.IsDrawerFront ||
        operationGroup.IsDoorPullout ||
        operationGroup.IsTopHangDoor ||
        operationGroup.IsBifoldLeftDoor ||
        operationGroup.IsBifoldRightDoor ||
        operationGroup.IsLeftBifoldExtension ||
        operationGroup.IsLeftApplianceMainDoor ||
        operationGroup.IsLeftApplianceExtendedDoor ||
        operationGroup.IsRightApplianceMainDoor ||
        operationGroup.IsRightApplianceExtendedDoor ||
        operationGroup.IsCorFoldKitLeftDoor ||
        operationGroup.IsCorFoldKitRightDoor ||
        partId.includes('drawerfront')
    );
};

export const tagDoorOrDrawerFront = (object: THREE.Object3D): void => {
    object.userData.isDoorOrDrawerFront = true;
};

export const setCabinetFrontsVisible = (
    cabinet: THREE.Object3D,
    visible: boolean
): void => {
    cabinet.traverse((child) => {
        if (child.userData?.isDoorOrDrawerFront) {
            child.visible = visible;
        }
    });
};

export const poseCabinetDoors = (
    cabinet: CabinetMesh,
    open: boolean,
    trigger: DoorPoseTrigger,
    animationSpeed?: number
): Promise<void> => {
    if (cabinet.userData.noInteractions) return Promise.resolve();

    cabinet.userData.isDoorOrDrawerOpen = open;
    return Promise.resolve(
        trigger(cabinet, 'toggleDoor', {
            doorGap: cabinet.userData.values?.cabinet_door_gap,
            isDoorOrDrawerOpen: open,
            animationSpeed,
        })
    );
};

export const applyDoorFrontsMode = (
    cabinet: CabinetMesh,
    mode: DoorFrontsMode,
    trigger: DoorPoseTrigger,
    options?: {animate?: boolean; animationSpeed?: number}
): Promise<void> => {
    setCabinetFrontsVisible(cabinet, mode !== 'hide');
    if (mode === 'hide' || !options?.animate) return Promise.resolve();

    return poseCabinetDoors(
        cabinet,
        mode === 'open',
        trigger,
        options.animationSpeed
    );
};
